import { apiRootPath, fetchAsync, postAsync } from 'common/fetch'
import { IBaseResponse } from 'types/baseTypes'
import {
  ArbitrationState,
  IArbitrationAttachmentRequest,
  IArbitrationDetail,
  IArbitrationDocumentSubmit,
  IArbitrationMetadata,
  IArbitrationResult,
  IArbitrationSubmitRequest
} from 'types/arbitrationTypes'

const BASE_PATH = `${apiRootPath}/account/arbitration`

interface IServerDropdownItem {
  ID: number
  Description: string
}

interface IServerMetadataResponse {
  FileSizeLimit: number
  AttachmentFormats: string[]
  MaximumMileageExceed: number
  MaxDocumentCount: number
  PhysicalDamageMinDocuments: number
  NonPhysicalDamageMinDocuments: number
  AttachmentsDisabled: boolean
  ContactMethods: IServerDropdownItem[]
  IssueTypes: IServerDropdownItem[]
  ResolutionTypes: IServerDropdownItem[]
}

interface IServerDocumentInfo {
  ID: number
  FileName: string
}

interface IServerDetailResponse {
  SaleInformation: {
    VIN: string
    ModelYear: string
    Model: string
    Mileage: number
    Grade: string
    SalePrice: number
    SaleDate: string
    Announcements: string[]
  }
  ContactInformation: {
    ContactName: string
    ContactEmail: string
    PhoneNumber: string
    ContactMethodID: number
  }
  ArbitrationInformation: {
    ArbitrationID: number
    SalesforceCaseID: string | null
    IssueTypeID: number
    ResolutionTypeID: number
    CurrentMileage: number
    Reason: string
    Documents: IServerDocumentInfo[] | null
  }
}

interface IServerArbitrationResult {
  State: ArbitrationState
}

interface IServerDocumentSubmit {
  FileName: string
  FileContent: string
}

interface IServerSubmitRequest {
  VehicleInstanceID: number
  BuyerVehiclePurchaseID: number
  VIN: string
  ContactName: string
  ContactEmail: string
  PhoneNumber: string
  ContactMethodID: number
  IssueTypeID: number
  ResolutionTypeID: number
  CurrentMileage: number
  Reason: string
  Documents: IServerDocumentSubmit[]
}

interface IServerAttachmentRequest {
  VehicleInstanceID: number
  ArbitrationID: number
  SalesforceCaseID: string
  VIN: string
  Documents: IServerDocumentSubmit[]
}

const mapDocumentSubmit = (document: IArbitrationDocumentSubmit): IServerDocumentSubmit => ({
  FileName: document.fileName,
  FileContent: document.fileContent
})

const mapMetadata = (response: IServerMetadataResponse): IArbitrationMetadata => ({
  fileSizeLimit: response.FileSizeLimit,
  attachmentFormats: response.AttachmentFormats,
  maxMileageExceed: response.MaximumMileageExceed,
  maxDocumentCount: response.MaxDocumentCount,
  physicalDamageMinDocuments: response.PhysicalDamageMinDocuments,
  nonPhysicalDamageMinDocuments: response.NonPhysicalDamageMinDocuments,
  attachmentsDisabled: response.AttachmentsDisabled,
  contactMethods: response.ContactMethods.map((item) => ({ id: item.ID, description: item.Description })),
  issueTypes: response.IssueTypes.map((item) => ({ id: item.ID, description: item.Description })),
  resolutionTypes: response.ResolutionTypes.map((item) => ({ id: item.ID, description: item.Description }))
})

const mapDetail = (response: IServerDetailResponse): IArbitrationDetail => ({
  vehicleArbitrationID: response.ArbitrationInformation?.ArbitrationID ?? null,
  salesforceCaseID: response.ArbitrationInformation?.SalesforceCaseID ?? null,
  arbitrationState: response.ArbitrationInformation?.ArbitrationID ? ArbitrationState.Submitted : ArbitrationState.None,
  latestArbitrationStatus: null,
  contactName: response.ContactInformation?.ContactName ?? '',
  contactEmail: response.ContactInformation?.ContactEmail ?? '',
  contactPhone: response.ContactInformation?.PhoneNumber ?? '',
  contactMethodID: response.ContactInformation?.ContactMethodID ?? 0,
  issueTypeID: response.ArbitrationInformation?.IssueTypeID ?? 0,
  resolutionTypeID: response.ArbitrationInformation?.ResolutionTypeID ?? 0,
  currentMileage: response.ArbitrationInformation?.CurrentMileage ?? 0,
  reason: response.ArbitrationInformation?.Reason ?? '',
  vin: response.SaleInformation?.VIN ?? '',
  modelYear: response.SaleInformation?.ModelYear ?? '',
  model: response.SaleInformation?.Model ?? '',
  grade: response.SaleInformation?.Grade ?? '',
  saleMileage: response.SaleInformation?.Mileage ?? 0,
  salePrice: response.SaleInformation?.SalePrice ?? 0,
  saleDate: response.SaleInformation?.SaleDate ?? '',
  announcements: response.SaleInformation?.Announcements ?? [],
  existingDocuments:
    response.ArbitrationInformation?.Documents?.map((document) => ({
      id: document.ID,
      fileName: document.FileName
    })) ?? []
})

const getResponseValue = <T>(response: IBaseResponse<T>, fallbackMessage: string): T => {
  if (!response?.Success || response.Value == null) {
    throw new Error(response?.ErrorMessages?.[0] || fallbackMessage)
  }

  return response.Value
}

export const getArbitrationMetadata = async (): Promise<IArbitrationMetadata> => {
  const response = await fetchAsync<IBaseResponse<IServerMetadataResponse>>(`${BASE_PATH}/metadata`)
  return mapMetadata(getResponseValue(response, 'Failed to load arbitration metadata.'))
}

export const getArbitrationInfo = async (vehicleInstanceID: number): Promise<IArbitrationDetail> => {
  const response = await fetchAsync<IBaseResponse<IServerDetailResponse>>(
    `${BASE_PATH}?vehicleInstanceID=${vehicleInstanceID}`
  )
  return mapDetail(getResponseValue(response, 'Failed to load arbitration details.'))
}

export const submitArbitration = async (request: IArbitrationSubmitRequest): Promise<IArbitrationResult> => {
  const response = await postAsync<IBaseResponse<IServerArbitrationResult>, IServerSubmitRequest>(BASE_PATH, {
    body: {
      VehicleInstanceID: request.vehicleInstanceID,
      BuyerVehiclePurchaseID: request.buyerVehiclePurchaseID,
      VIN: request.vin,
      ContactName: request.contactName,
      ContactEmail: request.contactEmail,
      PhoneNumber: request.contactPhone,
      ContactMethodID: request.contactMethodID,
      IssueTypeID: request.issueTypeID,
      ResolutionTypeID: request.resolutionTypeID,
      CurrentMileage: request.currentMileage,
      Reason: request.reason,
      Documents: request.documents.map(mapDocumentSubmit)
    }
  })

  return {
    success: response.Success,
    arbitrationState: response.Value?.State ?? ArbitrationState.New,
    salesforceCaseID: '',
    message: response.ErrorMessages?.[0] || ''
  }
}

export const addArbitrationDocuments = async (request: IArbitrationAttachmentRequest): Promise<IArbitrationResult> => {
  const response = await postAsync<IBaseResponse<string>, IServerAttachmentRequest>(`${BASE_PATH}/attachments`, {
    body: {
      VehicleInstanceID: request.vehicleInstanceID,
      ArbitrationID: request.arbitrationID,
      SalesforceCaseID: request.salesforceCaseID,
      VIN: request.vin,
      Documents: request.documents.map(mapDocumentSubmit)
    }
  })

  return {
    success: response.Success,
    arbitrationState: ArbitrationState.Submitted,
    salesforceCaseID: request.salesforceCaseID,
    message: response.ErrorMessages?.[0] || ''
  }
}
